// Translates between a link into /assignments and the assignment list's filters.
//
// The VMS import's failure notification links here: a failed inbound row names a client and a
// consultant, and whoever reads that email has to land on that person's assignments to fix the
// mapping. The parameter names match the filter fields themselves (clientId, userId) so a link
// built outside the app needs no translation table.

import { parseQueryId, setOrDeleteQueryParam, hasQueryChanged } from '@/lib/pagingAndSorting'

export const ASSIGNMENT_FILTER_QUERY_KEYS = ['clientId', 'userId']

/**
 * Reads the assignment list's filters out of a route query.
 *
 * A filter the query says nothing valid about comes back null. Callers apply only the non-null
 * ones, so opening /assignments with no parameters leaves whatever filters were already in the
 * store alone.
 *
 * @param {object} query - Vue Router route.query
 * @returns {{ clientId: number|null, userId: number|null }}
 */
export function parseAssignmentFiltersFromQuery (query = {}) {
  const { clientId, userId } = query || {}

  return {
    clientId: parseQueryId(clientId),
    userId: parseQueryId(userId)
  }
}

/**
 * Builds the next route query describing the filters currently applied to the list.
 *
 * Query params this module does not own are carried through untouched, and a filter that is not
 * applied is left out rather than written as an empty value, so the URL stays readable.
 *
 * @param {object} currentQuery - Vue Router route.query, not mutated
 * @param {{ clientId: *, userId: * }} filters - the filters the list is showing
 * @returns {object}
 */
export function buildAssignmentFiltersQuery (currentQuery, { clientId, userId }) {
  const nextQuery = { ...currentQuery }
  const values = { clientId, userId }

  ASSIGNMENT_FILTER_QUERY_KEYS.forEach(key => {
    // An empty string would otherwise be written as one, since setOrDeleteQueryParam only treats
    // null and undefined as absent.
    const value = values[key] === '' ? null : values[key]

    setOrDeleteQueryParam(nextQuery, key, value)
  })

  return nextQuery
}

/**
 * True when the query already says exactly what the given filters say.
 *
 * This is how a screen tells its own writes apart from somebody else's. Applying a filter rewrites
 * the URL, so a route watcher would otherwise treat the screen's own rewrite as an incoming link and
 * reapply it. A query built from the filters that are already applied is, by definition, the one
 * this screen just wrote.
 *
 * @param {object} query - Vue Router route.query
 * @param {{ clientId: *, userId: * }} filters - the filters currently applied to the list
 * @returns {boolean}
 */
export function queryDescribesFilters (query, filters) {
  const applied = buildAssignmentFiltersQuery(query, filters)

  return !hasQueryChanged(query, applied, ASSIGNMENT_FILTER_QUERY_KEYS)
}
